import { batch, createMemo, createSignal, untrack } from 'solid-js'
import type { FormControllerBindingType, FormControllerType, FormPublishType, FormValuesType, UseFormOptionsType } from '~/types/useForm'
import { collectIssues, visibleErrors } from '~/utils/formErrors'

const resolveInitial = (initial: UseFormOptionsType['initialValues']): FormValuesType => {
  const resolved = typeof initial === 'function' ? initial() : initial
  return resolved ?? {}
}

const sameValues = (a: FormValuesType, b: FormValuesType): boolean => {
  try {
    return JSON.stringify(a) === JSON.stringify(b)
  } catch {
    return false
  }
}

/**
 * Estado de un formulario, para cuando hace falta leerlo o tocarlo desde fuera
 * del `<form>`: deshabilitar el botón de guardar que vive en el pie de un Modal,
 * pintar el error de cada campo, o enviar desde otro sitio.
 *
 * `<Form>` solo se encarga del DOM; el controlador es quien sostiene los valores,
 * los errores y el estado de envío.
 *
 * ```tsx
 * const form = useForm({ schema, initialValues: () => cliente(), onSubmit: guardar })
 *
 * <Form controller={form}>
 *   <Input name="nombre" label="Nombre" error={form.error('nombre')} />
 * </Form>
 * <Button onClick={form.submit} disabled={!form.valid()} loading={form.submitting()}>Guardar</Button>
 * ```
 */
export function useForm<T extends FormValuesType = FormValuesType>(options: UseFormOptionsType<T> = {}): FormControllerType<T> {
  const [values, setValuesSignal] = createSignal<Partial<T>>(resolveInitial(options.initialValues) as Partial<T>)
  const [issues, setIssues] = createSignal<Record<string, string>>({})
  const [manualErrors, setManualErrors] = createSignal<Record<string, string>>({})
  const [touched, setTouched] = createSignal<string[]>([])
  const [valid, setValid] = createSignal(true)
  const [submitting, setSubmitting] = createSignal(false)
  const [submitted, setSubmitted] = createSignal(false)

  let binding: FormControllerBindingType | null = null
  let baseline: FormValuesType = { ...resolveInitial(options.initialValues) }

  /**
   * Último valor conocido de cada campo. Cubre dos casos: los que no tienen
   * elemento en el DOM (chips, listas, un color) y los que dejan de tenerlo (una
   * pestaña que se cambia, un bloque dentro de un `<Show>`). `<Form>` los mezcla
   * por debajo de lo que recoge, así que el DOM siempre manda sobre ellos.
   *
   * NO es una señal a propósito: `<Form>` la lee dentro de un efecto y, si fuera
   * reactiva, escribirla volvería a disparar ese efecto en bucle.
   */
  let extraValues: FormValuesType = { ...resolveInitial(options.initialValues) }
  const mergeExtra = (values: FormValuesType) => {
    extraValues = { ...extraValues, ...values }
  }

  /**
   * Sin `<Form>` no hay DOM del que recoger, así que el controlador valida por
   * su cuenta. Es lo que permite usarlo en pantallas cuyo formulario no es un
   * `<form>` — un constructor con panel lateral, por ejemplo.
   */
  const validateStandalone = (nextValues: FormValuesType) => {
    if (binding || !options.schema) return

    const result = options.schema.safeParse(nextValues)
    batch(() => {
      setIssues(result.success ? {} : collectIssues(result.error))
      setValid(result.success)
    })
  }

  // Los errores de Zod sólo se enseñan cuando el campo ya se tocó, o cuando ya
  // se intentó enviar. Los puestos a mano (los de la API) se ven siempre.
  const errors = createMemo<Record<string, string>>(() => visibleErrors(issues(), touched(), submitted(), manualErrors()))

  const dirty = createMemo(() => !sameValues(values() as FormValuesType, baseline))

  const controller: FormControllerType<T> = {
    values,
    errors,
    valid,
    dirty,
    submitting,
    submitted,

    error: name => errors()[name],

    setValue(name, value) {
      setValuesSignal(prev => ({ ...prev, [name]: value }) as Partial<T>)
      mergeExtra({ [name]: value })
      binding?.setFieldValue(name, value)
      // untrack: esto puede correr dentro de un efecto de <Form>, y leer la señal
      // aquí la haría dependencia de ese efecto, que a su vez la escribe.
      if (!binding) validateStandalone(untrack(values) as FormValuesType)
    },

    setValues(next) {
      setValuesSignal(prev => ({ ...prev, ...next }))
      mergeExtra(next as FormValuesType)
      binding?.applyValues(next as FormValuesType)
      if (!binding) validateStandalone(untrack(values) as FormValuesType)
    },

    setErrors(next) {
      setManualErrors(next)
    },

    reset(next) {
      const target = (next ?? resolveInitial(options.initialValues)) as Partial<T>
      baseline = { ...target }
      setValuesSignal(() => ({ ...target }))
      extraValues = { ...(target as FormValuesType) }
      setIssues({})
      setManualErrors({})
      setTouched([])
      setSubmitted(false)
      binding?.applyValues(target as FormValuesType)
      binding?.resetFields()
      validateStandalone(target as FormValuesType)
    },

    submit() {
      if (binding) {
        binding.requestSubmit()
        return
      }

      // Sin `<Form>`: el propio controlador valida y envía.
      setSubmitted(true)
      const current = untrack(values) as FormValuesType
      validateStandalone(current)

      if (options.schema) {
        const result = options.schema.safeParse(current)
        if (!result.success) return
        void controller.__submit(result.data as FormValuesType, new FormData())
        return
      }

      void controller.__submit(current, new FormData())
    },

    get options() {
      return options
    },

    __extra: () => extraValues,

    __bind(next) {
      binding = next
    },

    __publish({ values: nextValues, issues: nextIssues, valid: nextValid }: FormPublishType) {
      mergeExtra(nextValues)
      batch(() => {
        setValuesSignal(() => nextValues as Partial<T>)
        setIssues(nextIssues)
        setValid(nextValid)
      })
      // Un error de la API deja de tener sentido en cuanto el campo cambia
      setManualErrors(prev => {
        const remaining: Record<string, string> = {}
        for (const [name, message] of Object.entries(prev)) {
          if (nextValues[name] === baseline[name]) remaining[name] = message
        }
        return remaining
      })
    },

    __touch(name) {
      setTouched(prev => (prev.includes(name) ? prev : [...prev, name]))
    },

    __touchAll() {
      setSubmitted(true)
    },

    async __submit(nextValues, formData) {
      if (!options.onSubmit) return

      setSubmitting(true)
      try {
        await options.onSubmit(nextValues as T, formData)
        baseline = { ...nextValues }
      } catch (error) {
        if (!options.onError) throw error
        options.onError(error)
      } finally {
        setSubmitting(false)
      }
    }
  }

  return controller
}

export default useForm
